import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { Clock, ArrowLeft, CheckCircle, Send } from 'lucide-react'
import Navigation from '../../components/common/Navigation'
import Footer from '../../components/common/Footer'
import QuizInterface from '../../components/quiz/QuizInterface'
import { bankQuestionService, BankQuizData } from '../../services/bankQuestionService_moi'
import { useAuth } from '../../contexts/AuthContext'
import { teacherContentService } from '../../services/teacher/TeacherContentService'
import { databaseAvailabilityService } from '../../services/teacher/DatabaseAvailabilityService'

const LamTracNghiem: React.FC = () => {
  const { lop, chuong, bai } = useParams<{ lop: string; chuong: string; bai: string }>()
  const [searchParams] = useSearchParams()
  const teacherId = searchParams.get('teacher') // Lấy teacher ID từ query param
  const navigate = useNavigate()
  const { checkStudentInfo } = useAuth()
  const [quizData, setQuizData] = useState<BankQuizData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isQuizFullscreen, setIsQuizFullscreen] = useState(false)
  const [showQuizInterface, setShowQuizInterface] = useState(false)

  useEffect(() => {
    const loadQuiz = async () => {
      if (!lop || !chuong || !bai) {
        setError('Thiếu thông tin lớp, chương, bài')
        setLoading(false)
        return
      }

      setLoading(true)
      setError(null)
      console.log(`🎯 [START] Loading quiz for L${lop}_C${chuong}_B${bai}`, teacherId ? `(Teacher: ${teacherId})` : '(System)')

      try {
        let quiz: BankQuizData

        // ✅ FIX: Nếu có teacherId, ưu tiên load từ server (giống XemBaiHoc)
        if (teacherId) {
          console.log('👨‍🏫 [STEP 1] Loading quiz from teacher database...')

          // ✅ FIX: Check database availability trước (sử dụng cache)
          const availability = await databaseAvailabilityService.checkDatabaseAvailability(teacherId, lop, chuong, bai)
          console.log('📊 [STEP 1.0] Database availability:', availability)

          let tnDb: Uint8Array | null = null
          let dsDb: Uint8Array | null = null
          let tlDb: Uint8Array | null = null

          try {
            // ✅ FIX: Chỉ load từ server nếu có database từ server
            if (availability.hasServerDatabase) {
              console.log('📥 [STEP 1.0] Fetching databases from server...')
              try {
                const serverDbs = await teacherContentService.getTeacherDatabases(teacherId, lop, chuong, bai, 3000) // 3s timeout
                tnDb = serverDbs.TN
                dsDb = serverDbs.DS
                tlDb = serverDbs.TL

                console.log('📊 [STEP 1.0] Server databases result:', {
                  TN: tnDb ? `${tnDb.length} bytes` : 'null',
                  DS: dsDb ? `${dsDb.length} bytes` : 'null',
                  TL: tlDb ? `${tlDb.length} bytes` : 'null'
                })
              } catch (serverError) {
                console.warn('⚠️ [STEP 1.0] Failed to fetch databases from server:', serverError)
              }
            } else {
              console.log('⚠️ [STEP 1.0] No server database available (from cache), skipping server fetch')
            }

            // ✅ FIX: Nếu không có từ server, thử load từ IndexedDB (fallback)
            if ((!tnDb || tnDb.length === 0) && (!dsDb || dsDb.length === 0) && (!tlDb || tlDb.length === 0)) {
              if (availability.hasIndexedDB) {
                console.log('⚠️ [STEP 1.1] No server databases found, checking local IndexedDB...')
              } else {
                console.log('⚠️ [STEP 1.1] No server databases and no IndexedDB (from cache), skipping IndexedDB check')
              }

              console.log('📦 [STEP 1.1] Loading TN database from IndexedDB...')
              tnDb = await Promise.race([
                teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'TN'),
                new Promise<null>((_, reject) => setTimeout(() => reject(new Error('TN timeout')), 5000))
              ]).catch(() => {
                console.warn('⚠️ TN database load failed or timeout')
                return null
              })
              console.log('📦 [STEP 1.1] TN result:', tnDb ? `${tnDb.length} bytes` : 'null')

              console.log('📦 [STEP 1.2] Loading DS database from IndexedDB...')
              dsDb = await Promise.race([
                teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'DS'),
                new Promise<null>((_, reject) => setTimeout(() => reject(new Error('DS timeout')), 5000))
              ]).catch(() => {
                console.warn('⚠️ DS database load failed or timeout')
                return null
              })
              console.log('📦 [STEP 1.2] DS result:', dsDb ? `${dsDb.length} bytes` : 'null')

              console.log('📦 [STEP 1.3] Loading TL database from IndexedDB...')
              tlDb = await Promise.race([
                teacherContentService.loadDatabaseFromIndexedDB(teacherId, lop, chuong, bai, 'TL'),
                new Promise<null>((_, reject) => setTimeout(() => reject(new Error('TL timeout')), 5000))
              ]).catch(() => {
                console.warn('⚠️ TL database load failed or timeout')
                return null
              })
              console.log('📦 [STEP 1.3] TL result:', tlDb ? `${tlDb.length} bytes` : 'null')

              console.log('📊 [STEP 1.1-1.3] IndexedDB databases result:', {
                TN: tnDb ? `${tnDb.length} bytes` : 'null',
                DS: dsDb ? `${dsDb.length} bytes` : 'null',
                TL: tlDb ? `${tlDb.length} bytes` : 'null'
              })
            }

            // Chỉ dùng teacher database nếu thực sự có data (không phải null)
            const hasTeacherData = (tnDb && tnDb.length > 0) || (dsDb && dsDb.length > 0) || (tlDb && tlDb.length > 0)

            if (hasTeacherData) {
              console.log('✅ [STEP 2] Teacher databases found, loading questions...')
              quiz = await Promise.race([
                bankQuestionService.getRandomQuestions(lop, chuong, bai, {
                  thoiGianLamBai: 15,
                  diemDat: 70
                }, teacherId, { TN: tnDb, DS: dsDb, TL: tlDb }),
                new Promise<BankQuizData>((_, reject) =>
                  setTimeout(() => reject(new Error('getRandomQuestions timeout')), 30000)
                )
              ])
              console.log('✅ [STEP 2] Teacher quiz loaded:', quiz?.cauHoi?.length || 0, 'questions')
            } else {
              console.log('⚠️ [STEP 2] No teacher databases found or empty, falling back to system')
              quiz = await Promise.race([
                bankQuestionService.getRandomQuestions(lop, chuong, bai, {
                  thoiGianLamBai: 15,
                  diemDat: 70
                }),
                new Promise<BankQuizData>((_, reject) =>
                  setTimeout(() => reject(new Error('getRandomQuestions timeout')), 30000)
                )
              ])
              console.log('✅ [STEP 2] System quiz loaded:', quiz?.cauHoi?.length || 0, 'questions')
            }
          } catch (teacherError: any) {
            console.error('❌ [STEP 2] Error loading teacher databases, falling back to system:', teacherError)
            // Fallback to system on any error
            quiz = await Promise.race([
              bankQuestionService.getRandomQuestions(lop, chuong, bai, {
                thoiGianLamBai: 15,
                diemDat: 70
              }),
              new Promise<BankQuizData>((_, reject) =>
                setTimeout(() => reject(new Error('getRandomQuestions timeout')), 30000)
              )
            ])
            console.log('✅ [STEP 2] Fallback system quiz loaded:', quiz?.cauHoi?.length || 0, 'questions')
          }
        } else {
          // Load từ bank hệ thống
          console.log('📚 [STEP 1] Loading from system database...')
          quiz = await Promise.race([
            bankQuestionService.getRandomQuestions(lop, chuong, bai, {
              thoiGianLamBai: 15,  // 15 phút
              diemDat: 70          // 70% để đạt
            }),
            new Promise<BankQuizData>((_, reject) =>
              setTimeout(() => reject(new Error('getRandomQuestions timeout')), 30000)
            )
          ])
          console.log('✅ [STEP 1] System quiz loaded:', quiz?.cauHoi?.length || 0, 'questions')
        }

        console.log('✅ [END] Quiz loaded successfully:', {
          totalQuestions: quiz?.cauHoi?.length || 0,
          title: quiz?.tieuDe
        })
        setQuizData(quiz)
      } catch (error: any) {
        console.error('❌ [ERROR] Error loading quiz:', error)
        setError(`Không thể tải trắc nghiệm: ${error?.message || 'Unknown error'}`)
      } finally {
        console.log('🏁 [FINALLY] Setting loading to false')
        setLoading(false)
      }
    }

    loadQuiz()
  }, [lop, chuong, bai, teacherId])

  const handleBack = () => {
    navigate('/trac-nghiem')
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {!isQuizFullscreen && <Navigation />}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Đang tải trắc nghiệm...</p>
            <p className="text-sm text-gray-500 mt-2">Lớp {lop} - Chương {chuong} - Bài {bai}</p>
          </div>
        </div>
        {!isQuizFullscreen && <Footer />}
      </div>
    )
  }

  if (error || !quizData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {!isQuizFullscreen && <Navigation />}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Không thể tải trắc nghiệm</h2>
            <p className="text-gray-600 mb-6">{error || 'Đã xảy ra lỗi'}</p>
            <button
              onClick={handleBack}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Quay lại danh sách</span>
            </button>
          </div>
        </div>
        {!isQuizFullscreen && <Footer />}
      </div>
    )
  }

  if (quizData.cauHoi.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        {!isQuizFullscreen && <Navigation />}
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="text-yellow-500 text-6xl mb-4">📝</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Chưa có câu hỏi</h2>
            <p className="text-gray-600 mb-6">Bài học này chưa có câu hỏi trắc nghiệm</p>
            <button
              onClick={handleBack}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Quay lại danh sách</span>
            </button>
          </div>
        </div>
        {!isQuizFullscreen && <Footer />}
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Ẩn Navigation khi bắt đầu làm bài hoặc fullscreen */}
      {!showQuizInterface && !isQuizFullscreen && <Navigation />}

      {/* Hiển thị QuizInterface khi bắt đầu làm bài */}
      {showQuizInterface && (
        <QuizInterface
          quizData={quizData}
          lessonId={`L${lop}_C${chuong}_B${bai}`}
          onBack={handleBack}
          onFullscreenChange={setIsQuizFullscreen}
          forceDarkMode={true}
        />
      )}

      {/* Hiển thị thông tin bài kiểm tra khi chưa bắt đầu làm bài */}
      {!showQuizInterface && !isQuizFullscreen && (
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-6">
            {/* Navigation Row - Back button */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={handleBack}
                className="inline-flex items-center space-x-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Quay lại danh sách trắc nghiệm</span>
                <span className="sm:hidden">Quay lại</span>
              </button>
            </div>

            {/* Quiz Information - Hidden when quiz is active */}
            {!showQuizInterface && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">{quizData.tieuDe}</h1>
                    <p className="text-gray-600">{quizData.moTa}</p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center space-x-2 text-gray-600 mb-2">
                      <Clock className="w-5 h-5" />
                      <span className="font-semibold">{Math.floor(quizData.gioi_han_thoi_gian / 60)} phút</span>
                    </div>
                    <div className="flex items-center space-x-2 text-gray-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="text-sm">Điểm đạt: {quizData.diem_dat}%</span>
                    </div>
                  </div>
                </div>

                {/* Start Quiz Button */}
                <div className="mt-6 text-center">
                  <button
                    onClick={async () => {
                      // ✅ FIX: Set showQuizInterface ngay lập tức để tránh treo UI
                      setShowQuizInterface(true)

                      // ✅ Check student info sau khi đã hiển thị giao diện (non-blocking)
                      // Nếu cần thông tin, QuizInterface sẽ tự xử lý
                      checkStudentInfo().catch(err => {
                        console.error('Error checking student info:', err)
                      })
                    }}
                    className="inline-flex items-center space-x-2 px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl"
                  >
                    <span>Làm bài</span>
                  </button>
                </div>
              </div>
            )}

            {/* Quiz Interface - Always show when quiz is active */}
          </div>
        </div>
      )}

      {/* Quiz Interface - Fullscreen mode */}
      {isQuizFullscreen && (
        <QuizInterface
          quizData={quizData}
          lessonId={`L${lop}_C${chuong}_B${bai}`}
          onBack={handleBack}
          onFullscreenChange={setIsQuizFullscreen}
          forceDarkMode={true}
        />
      )}

      {/* Ẩn Footer khi bắt đầu làm bài hoặc fullscreen */}
      {!showQuizInterface && !isQuizFullscreen && <Footer />}
    </div>
  )
}

export default LamTracNghiem

